/**
 * Trystero peer wrapper — joins a Trystero room and exposes the four
 * mesh actions (cap, cm, cf, tc) as typed send/receive methods.
 *
 * Codec frame transport (`cf`) goes over a separate Trystero action
 * with binary payloads — bytes pass through the WebRTC data channel
 * as-is, no JSON wrapping, no base64. That's the bandwidth-critical
 * path for cross-peer LLM streaming.
 */
import { decodeFrameBytes, encodeFrameBytes } from './wire.js';
import { decodeEnvelope, encodeEnvelope, } from './webrtc-codec.js';
import { isMeshChatMessage, isMeshPeerCap, isMeshToolFrame, } from './guards.js';
import { Roster } from './roster.js';
import { installIceDiagnostics } from './iceDiagnostics.js';
export function splitPeerTarget(peers, selfId) {
    if (peers === undefined)
        return { loopback: false, remote: undefined };
    if (typeof peers === 'string') {
        return peers === selfId ? { loopback: true, remote: [] } : { loopback: false, remote: peers };
    }
    return { loopback: peers.includes(selfId), remote: peers.filter((p) => p !== selfId) };
}
// ── joinMesh ──────────────────────────────────────────────────────────────
/**
 * Connect to a Trystero room with the five mesh actions wired up.
 * Returns the `Peer` handle the consumer interacts with.
 *
 * Action mapping:
 *
 *   cap  →  MeshPeerCap (JSON, small; broadcast on join + heartbeat)
 *   cm   →  MeshChatMessage (JSON; small chat metadata + body)
 *   cf   →  Uint8Array — either a raw Codec msgpack frame OR a full
 *           WebRtcEnvelope-encoded message. Receivers try
 *           `decodeEnvelope` first; on miss, fall back to
 *           `decodeFrameBytes` for backward-compat with pre-envelope
 *           peers. New peers should always emit envelopes.
 *   tc   →  MeshToolFrame ({kind: 'call' | 'result'}) — also carries
 *           Phase C stage-control messages (stage.load/ready/stop/
 *           ping/pong/progress/token), see `stageControl.ts`.
 *   sf   →  Uint8Array — Phase C pipeline-split activation-wire frame
 *           (see `stageOrchestrator.ts`). Bytes only, no fallback
 *           decode — a v1-only peer never calls `onStageFrame` and
 *           Trystero simply has no receiver-side listener for it.
 */
export function joinMesh(opts) {
    const { joinRoom, selfId, trysteroConfig, roomId, cap: initialCap } = opts;
    const heartbeatMs = opts.heartbeatMs ?? 30_000;
    // Observe every WebRTC connection attempt (incl. ones that never
    // complete — invisible to trystero's own API); see iceDiagnostics.ts.
    // Must run BEFORE joinRoom constructs any RTCPeerConnection.
    installIceDiagnostics();
    const room = joinRoom(trysteroConfig, roomId, {
        onJoinError: (d) => {
            // eslint-disable-next-line no-console
            console.error('[legion-mesh] joinRoom error:', d);
        },
    });
    // Skip prune while the tab is hidden — Chrome throttles setInterval
    // to ~1/min on backgrounded tabs, heartbeats miss the prune window,
    // and you come back to an empty roster. Detect via document.visibilityState
    // when the API is available (browser), else always run (Node bridge).
    const isHidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';
    const roster = new Roster({ isPaused: isHidden });
    let currentCap = initialCap;
    // ── Actions ──────────────────────────────────────────────────────────
    const [sendCap, onCap] = room.makeAction('cap');
    const [sendChat, onChat] = room.makeAction('cm');
    const [sendFrameBytes, onFrameBytes] = room.makeAction('cf');
    const [sendTool, onTool] = room.makeAction('tc');
    const [sendStageFrameBytes, onStageFrameBytes] = room.makeAction('sf');
    // ── Local listener registries ────────────────────────────────────────
    const chatListeners = new Set();
    const frameListeners = new Set();
    const envelopeListeners = new Set();
    const toolListeners = new Set();
    const stageFrameListeners = new Set();
    // ── Logging, two tiers ───────────────────────────────────────────────
    //
    // `debug` = LIFECYCLE, on by default. One line per thing that actually
    // happened: joined a room, a peer arrived or left, a frame failed its
    // guard. These are rare and are what you want in a bug report.
    //   window.__legion_debug = false     silences them.
    //
    // `trace` = PER-TICK CHATTER, off by default (2026-08-31). The heartbeat
    // fires every few seconds and each one printed a broadcast line plus a
    // `cap RECEIVED` line per peer. That buried every real signal in the
    // console. Chat receipts moved here too. That line printed MESSAGE TEXT
    // to the console on every inbound message. That has no business being on
    // by default.
    //   window.__legion_debug = true      turns them on mid-session.
    //
    // The flag is deliberately tri-state. `false` silences both tiers.
    // `true` enables both. Unset keeps lifecycle only.
    const debug = (...args) => {
        try {
            const flag = globalThis.__legion_debug;
            if (flag === false)
                return;
            // eslint-disable-next-line no-console
            console.info('[legion-mesh]', ...args);
        }
        catch {
            /* ignore */
        }
    };
    const trace = (...args) => {
        try {
            if (globalThis.__legion_debug !== true)
                return;
            // eslint-disable-next-line no-console
            console.info('[legion-mesh]', ...args);
        }
        catch {
            /* ignore */
        }
    };
    // ── Inbound wiring ───────────────────────────────────────────────────
    onCap((raw, peerId) => {
        if (!isMeshPeerCap(raw)) {
            debug('cap REJECTED from', peerId, '— guard failed; raw:', raw);
            return;
        }
        trace('cap RECEIVED from', peerId, '· nick=', raw.nick, '· tools=', raw.tools.length);
        roster.upsert(peerId, raw);
    });
    onChat((raw, peerId) => {
        trace('chat RECEIVED from', peerId, '· text=', raw?.text);
        if (!isMeshChatMessage(raw)) {
            debug('chat REJECTED — guard failed; raw:', raw);
            return;
        }
        for (const cb of chatListeners)
            cb(raw, peerId);
    });
    onFrameBytes((raw, peerId) => {
        // Try envelope decode first (new format), fall back to bare-frame decode.
        const env = decodeEnvelope(raw);
        if (env) {
            for (const cb of envelopeListeners)
                cb(env, peerId);
            // If the envelope carries a DATA payload that's also a raw frame,
            // also surface it on the frame listener for backward-compat
            // consumers.
            if (env.k === 2 && env.b instanceof Uint8Array) {
                const frame = decodeFrameBytes(env.b);
                if (frame) {
                    for (const cb of frameListeners)
                        cb(frame, peerId);
                }
            }
            return;
        }
        // Pre-envelope peer: bare msgpack frame.
        const frame = decodeFrameBytes(raw);
        if (!frame)
            return;
        for (const cb of frameListeners)
            cb(frame, peerId);
    });
    onTool((raw, peerId) => {
        if (!isMeshToolFrame(raw))
            return;
        for (const cb of toolListeners)
            cb(raw, peerId);
    });
    onStageFrameBytes((raw, peerId) => {
        if (!(raw instanceof Uint8Array))
            return;
        for (const cb of stageFrameListeners)
            cb(raw, peerId);
    });
    // ── Peer join/leave: cap broadcast + roster prune ────────────────────
    room.onPeerJoin((peerId) => {
        debug('peer JOINED', peerId, '— sending our cap');
        void sendCap(currentCap, peerId);
    });
    room.onPeerLeave((peerId) => {
        debug('peer LEFT', peerId);
        roster.remove(peerId);
    });
    // ── Self in roster ───────────────────────────────────────────────────
    // Trystero's makeAction sender doesn't echo back to the local
    // receiver — sending our cap only reaches REMOTE peers. So we'd
    // never see ourselves in our own roster, which means our own tools
    // never show up in the aggregated public-tools list either. Mirror
    // leet's pattern: upsert self locally whenever we broadcast our cap.
    const upsertSelf = () => {
        roster.upsert(selfId, currentCap);
    };
    upsertSelf();
    // ── Initial + heartbeat cap broadcast ────────────────────────────────
    debug('joined room', roomId, '· selfId=', selfId, '· nick=', currentCap.nick);
    void sendCap(currentCap);
    const heartbeat = typeof setInterval !== 'undefined'
        ? setInterval(() => {
            currentCap = { ...currentCap, ts: Date.now() };
            trace('heartbeat cap broadcast');
            void sendCap(currentCap);
            upsertSelf();
        }, heartbeatMs)
        : null;
    // On tab return-to-foreground, immediately re-broadcast our cap so
    // other peers don't prune us, and bump every roster entry's
    // `lastSeen` so we don't prune them either while waiting on their
    // next heartbeat. Browser-only; Node bridge has no document.
    let visibilityHandler = null;
    if (typeof document !== 'undefined') {
        visibilityHandler = () => {
            if (document.visibilityState !== 'visible')
                return;
            trace('tab visible — re-broadcasting cap');
            currentCap = { ...currentCap, ts: Date.now() };
            void sendCap(currentCap);
            upsertSelf();
            // Give peers a grace window — bump their lastSeen so the next
            // prune doesn't fire on heartbeats missed during throttling.
            const now = Date.now();
            for (const entry of roster.snapshot()) {
                roster.upsert(entry.peerId, { ...entry, ts: now });
            }
        };
        document.addEventListener('visibilitychange', visibilityHandler);
    }
    // ── Public API ───────────────────────────────────────────────────────
    return {
        selfId,
        roster,
        setCap(next) {
            currentCap = { ...next, ts: Date.now() };
            void sendCap(currentCap);
            upsertSelf();
        },
        async sendChat(partial, peers) {
            const msg = {
                v: 1,
                ts: Date.now(),
                from: selfId,
                ...partial,
            };
            await sendChat(msg, peers);
        },
        onChat(cb) {
            chatListeners.add(cb);
            return () => chatListeners.delete(cb);
        },
        async sendFrame(frame, peers) {
            const bytes = encodeFrameBytes(frame);
            await sendFrameBytes(bytes, peers);
        },
        onFrame(cb) {
            frameListeners.add(cb);
            return () => frameListeners.delete(cb);
        },
        async sendEnvelope(env, peers) {
            const bytes = encodeEnvelope(env);
            await sendFrameBytes(bytes, peers);
        },
        onEnvelope(cb) {
            envelopeListeners.add(cb);
            return () => envelopeListeners.delete(cb);
        },
        async sendTool(frame, peers) {
            const { loopback, remote } = splitPeerTarget(peers, selfId);
            if (loopback) {
                for (const cb of toolListeners)
                    cb(frame, selfId);
            }
            if (remote === undefined || remote.length > 0) {
                await sendTool(frame, remote);
            }
        },
        onTool(cb) {
            toolListeners.add(cb);
            return () => toolListeners.delete(cb);
        },
        async sendStageFrame(bytes, peers) {
            const { loopback, remote } = splitPeerTarget(peers, selfId);
            if (loopback) {
                for (const cb of stageFrameListeners)
                    cb(bytes, selfId);
            }
            if (remote === undefined || remote.length > 0) {
                await sendStageFrameBytes(bytes, remote);
            }
        },
        onStageFrame(cb) {
            stageFrameListeners.add(cb);
            return () => stageFrameListeners.delete(cb);
        },
        async peerConnectionType(peerId) {
            try {
                if (typeof room.getPeers !== 'function')
                    return 'unknown';
                const pc = room.getPeers()[peerId];
                if (!pc || typeof pc.getStats !== 'function')
                    return 'unknown';
                // Same maplike-view cast iceDiagnostics.ts's captureSelectedPair
                // uses — RTCStatsReport IS a maplike at runtime, older lib.dom
                // typings just don't declare `.get`.
                const stats = (await pc.getStats());
                let result = 'unknown';
                stats.forEach((report) => {
                    if (report.type !== 'candidate-pair' || !report.nominated)
                        return;
                    const r = report;
                    const local = r.localCandidateId ? stats.get(r.localCandidateId) : undefined;
                    const remote = r.remoteCandidateId ? stats.get(r.remoteCandidateId) : undefined;
                    const localType = local?.candidateType;
                    const remoteType = remote?.candidateType;
                    if (localType === 'relay' || remoteType === 'relay') {
                        result = 'relayed';
                    }
                    else if ((localType === 'host' || localType === 'srflx' || localType === 'prflx') &&
                        (remoteType === 'host' || remoteType === 'srflx' || remoteType === 'prflx')) {
                        result = 'direct';
                    }
                });
                return result;
            }
            catch {
                // getStats is best-effort — never let observability break the mesh.
                return 'unknown';
            }
        },
        leave() {
            if (heartbeat)
                clearInterval(heartbeat);
            if (visibilityHandler && typeof document !== 'undefined') {
                document.removeEventListener('visibilitychange', visibilityHandler);
            }
            roster.dispose();
            room.leave();
        },
    };
}
